/**
 * JWT-Utilities für Dekodierung und Validierung.
 * Keine Signaturprüfung — nur strukturelle Validierung + exp-Claim-Auswertung.
 */

/**
 * Dekodiert den Payload eines JWT (base64url oder standard base64).
 * Akzeptiert beide Formate für Robustheit:
 * - base64url: - und _ statt + und /, kein Padding
 * - standard base64: + und /, optionales Padding
 *
 * @param token JWT im Format "header.payload.signature"
 * @returns Dekodiertes Payload-Objekt oder null bei Fehler
 */
export function decodeJwtPayload(token: string): Record<string, unknown> | null {
  if (!token || typeof token !== 'string') {
    return null
  }

  const parts = token.split('.')
  if (parts.length !== 3) {
    return null
  }

  const payloadB64 = parts[1]
  if (!payloadB64) {
    return null
  }

  try {
    // Konvertiere base64url zu standard base64
    const standardB64 = payloadB64.replace(/-/g, '+').replace(/_/g, '/')
    // Dekodiere
    const payload = atob(standardB64)
    return JSON.parse(payload) as Record<string, unknown>
  } catch {
    return null
  }
}

/**
 * Extrahiert den exp-Claim (Expiration Time in Sekunden seit Epoch) aus einem JWT.
 *
 * @param token JWT im Format "header.payload.signature"
 * @returns exp-Wert (in Sekunden) oder null falls nicht vorhanden/ungültig
 */
export function getJwtExp(token: string): number | null {
  const payload = decodeJwtPayload(token)
  if (!payload) {
    return null
  }

  const exp = payload.exp
  if (exp === undefined || exp === null) {
    return null
  }

  // exp muss eine Zahl sein
  if (typeof exp !== 'number') {
    return null
  }

  return exp
}

/**
 * Prüft, ob ein JWT abgelaufen ist.
 * Ein Token ist abgelaufen, wenn:
 * - Kein gültiges exp-Claim vorhanden ist, ODER
 * - exp*1000 (ms) <= nowMs (Vergleichszeitpunkt)
 *
 * @param token JWT im Format "header.payload.signature"
 * @param nowMs Vergleichszeitpunkt in Millisekunden (default: Date.now())
 * @returns true wenn abgelaufen oder ungültig, false wenn noch gültig
 */
export function isJwtExpired(token: string, nowMs: number = Date.now()): boolean {
  const exp = getJwtExp(token)
  if (exp === null) {
    return true
  }

  // exp ist in Sekunden, nowMs in Millisekunden
  return exp * 1000 <= nowMs
}
